import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Recycle, Truck, Broom, Wrench, RoadHorizon, Factory } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Recycle: Recycle, Truck: Truck, Broom: Broom, Wrench: Wrench, Road: RoadHorizon, Factory: Factory,
};

// Stable URL-safe slug used to deep-link to a specific service section on
// /services. Falls back to a known set of short ids for the canonical six
// services so nothing breaks if an admin edits a title.
const KNOWN_SLUGS = {
  'commercial waste collections': 'commercial-waste',
  'driver training academy': 'driver-training',
  'event cleansing services': 'event-cleansing',
  'commercial vehicle repair': 'vehicle-repair',
  'highways maintenance': 'highways',
  'waste transfer & disposal': 'waste-transfer',
};

export function serviceSlug(title) {
  if (!title) return '';
  const key = title.trim().toLowerCase();
  if (KNOWN_SLUGS[key]) return KNOWN_SLUGS[key];
  return key
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const defaultServices = [
  { id: 'commercial-waste', title: 'Commercial Waste Collections', description: 'Professional & Reliable Recycling and Waste Collection Services to Businesses across North Somerset.', icon: 'Recycle', image_url: 'https://images.unsplash.com/photo-1717667745830-de42bb75a4fa?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 'driver-training', title: 'Driver Training Academy', description: 'Unlock your potential with our professional HGV driver training courses.', icon: 'Truck', image_url: 'https://images.unsplash.com/photo-1759662232707-f1e0a4134c93?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 'event-cleansing', title: 'Event Cleansing Services', description: 'Maintain a clean and welcoming environment for all your events.', icon: 'Broom', image_url: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=1000' },
  { id: 'vehicle-repair', title: 'Commercial Vehicle Repair', description: 'Competitive, reliable, and cost-effective commercial vehicle maintenance.', icon: 'Wrench', image_url: 'https://images.unsplash.com/photo-1767340078306-89630b12d81c?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 'highways', title: 'Highways Maintenance', description: 'From pothole repairs to full carriageway repair across North Somerset.', icon: 'Road', image_url: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?q=80&w=1000' },
  { id: 'waste-transfer', title: 'Waste Transfer & Disposal', description: 'Supporting sustainable waste management with reduced carbon footprint.', icon: 'Factory', image_url: 'https://images.unsplash.com/photo-1754797007532-14d15c2eacdb?crop=entropy&cs=srgb&fm=jpg&q=85' },
];

export const ServicesBentoGrid = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`).then(r => {
      if (r.data && r.data.length > 0) {
        setServices(r.data.map((s, i) => ({ ...s, id: s.id || `service-${i}` })));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // "Halfway" layout: first card spans 2 columns on large screens (hero card),
  // every other card sits on a single column. auto-rows-fr keeps all rows equal
  // height so removing/adding services never breaks alignment.
  const getCardClass = (index) => index === 0
    ? 'lg:col-span-2 min-h-[300px]'
    : 'lg:col-span-1 min-h-[260px]';

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <p className="accent-font text-lime-600 mb-4">What We Do</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-6">
            Local Services, <span className="text-lime-600">Local Expertise</span>
          </h2>
          <p className="text-neutral-600 text-lg max-w-2xl">
            At NSEC, we offer a range of services tailored to meet your needs. 
            As a local company, we&apos;re always invested in the well-being of our community.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ staggerChildren: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr"
        >
          {loading ? (
            [0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`service-card-skeleton relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm animate-pulse h-full ${getCardClass(i)}`}
              >
                <div className="absolute inset-0 bg-neutral-50" />
              </div>
            ))
          ) : services.map((service, index) => {
            const IconComp = iconMap[service.icon] || Recycle;
            const isHero = index === 0;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`service-card group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm hover:shadow-xl h-full ${getCardClass(index)}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="absolute inset-0">
                  <img src={service.image_url} alt={service.title} className="w-full h-full object-cover opacity-45 group-hover:opacity-60 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/75 to-white/40" />
                </div>
                <Link to={`/services#${serviceSlug(service.title)}`} className="relative z-10 flex flex-col h-full p-6 lg:p-8" data-testid={`service-link-${serviceSlug(service.title)}`}>
                  <div className="mb-4">
                    <div className="inline-flex p-3 rounded-xl bg-lime-50">
                      {service.icon_url ? (
                        <img src={service.icon_url} alt="" className={`object-contain ${isHero ? 'w-10 h-10' : 'w-8 h-8'}`} />
                      ) : (
                        <IconComp size={isHero ? 40 : 32} weight="duotone" className="text-lime-600" />
                      )}
                    </div>
                  </div>
                  <div className="mt-auto">
                    <h3 className={`font-outfit font-semibold text-neutral-900 mb-3 ${isHero ? 'text-2xl lg:text-3xl' : 'text-xl lg:text-2xl'}`}>{service.title}</h3>
                    <p className={`text-neutral-600 mb-4 ${isHero ? 'text-base lg:text-lg max-w-2xl' : 'text-sm lg:text-base'}`}>{service.description}</p>
                    <span className="inline-flex items-center gap-2 text-lime-600 text-sm font-medium">
                      Learn more <ArrowRight size={16} weight="bold" className="service-arrow" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-12">
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="services-view-all">
            View All Services <ArrowRight size={20} weight="bold" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
